import { useState, useContext, useEffect, useRef, useMemo } from 'react'
import GlobalContext from '../../../../../../../context/global-context'
import { sileo } from 'sileo'
import { BankAPI } from '../../../../../../../api/bancos.api'
import organizationApi from '../../../../../../../api/organization.api'
import { currentMonth, currentYear, REACT_APP_RECONCILIATION_API_URL } from '../../../../../../utils/constant'
import { getSavedBankConciliationDate } from './automaticConciliation'

export const useAutomaticConciliation = ({ hasBankLinked, enabledState }) => {
  const { currentBusiness, setCurrentBusiness } = useContext(GlobalContext)
  const bankAPI = useMemo(() => new BankAPI(), [])
  const [isAutomaticConciliation, setIsAutomaticConciliation] = useState(false)
  const [isUpdatingAutomaticConciliation, setIsUpdatingAutomaticConciliation] = useState(false)
  const [isCheckingAutomaticConciliation, setIsCheckingAutomaticConciliation] = useState(false)
  const automaticConciliationClickPending = useRef(false)
  const activeBusinessRef = useRef(currentBusiness)
  activeBusinessRef.current = currentBusiness
  const [isRunningRemunerationConciliation, setIsRunningRemunerationConciliation] = useState(false)
  const [isRunningTransferConciliation, setIsRunningTransferConciliation] = useState(false)
  const [isRunningF29Conciliation, setIsRunningF29Conciliation] = useState(false)
  const [showAutoConciliationModal, setShowAutoConciliationModal] = useState(false)
  const [autoConciliationAction, setAutoConciliationAction] = useState(null)
  const [autoConciliationStep, setAutoConciliationStep] = useState(1)
  const [autoConciliationPeriod, setAutoConciliationPeriod] = useState('current-month')
  const [autoConciliationMonth, setAutoConciliationMonth] = useState(currentMonth)
  useEffect(() => {
    let isMounted = true

    const resetAutoConciliationModalState = () => {
      setShowAutoConciliationModal(false)
      setAutoConciliationAction(null)
      setAutoConciliationStep(1)
      setAutoConciliationPeriod('current-month')
      setAutoConciliationMonth(currentMonth)
    }

    const syncAutomaticConciliationState = async () => {
      resetAutoConciliationModalState()

      const currentBusinessState = enabledState ?? currentBusiness?.conciliacionAutomatica
      if (typeof currentBusinessState === 'boolean') {
        if (isMounted) setIsAutomaticConciliation(currentBusinessState)
        return
      }

      if (!currentBusiness?.id) {
        if (isMounted) setIsAutomaticConciliation(false)
        return
      }

      try {
        const response = await organizationApi.getMyBusinesses()
        const syncedBusiness = response?.data?.find(item => item.id === currentBusiness.id)
        if (isMounted) {
          setIsAutomaticConciliation(Boolean(syncedBusiness?.conciliacionAutomatica))
        }
      } catch (error) {
        console.error('Error obteniendo estado de conciliación automática:', error)
        if (isMounted) setIsAutomaticConciliation(false)
      }
    }

    syncAutomaticConciliationState()

    return () => {
      isMounted = false
    }
  }, [currentBusiness?.id, currentBusiness?.conciliacionAutomatica, enabledState])

  const getRemunerationReconciliationBankSummary = async businessId => {
    if (!businessId) return { items: [] }

    const params = new URLSearchParams({
      businessId: String(businessId)
    })

    const response = await fetch(
      `${REACT_APP_RECONCILIATION_API_URL}/remuneraciones/conciliacion/bank-summary?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      }
    )

    if (!response.ok) {
      throw new Error(
        `Error consultando conciliación bancaria de remuneraciones: ${response.status}`
      )
    }

    return response.json()
  }

  const persistRemunerationReconciliationsInLuca = async (
    businessId,
    items,
    allowedMonths
  ) => {
    if (!businessId || !Array.isArray(items) || items.length === 0) return

    const allowedMonthsSet = new Set(allowedMonths || [])
    const groupedByTarget = new Map()

    items.forEach(item => {
      const targetIdLibro = Number(item?.target?.idLibro)
      const bankIdLibro = Number(item?.idLibro)
      const period = String(item?.period || '')

      if (
        item?.isReconciled !== true ||
        !Number.isFinite(targetIdLibro) ||
        !Number.isFinite(bankIdLibro) ||
        !allowedMonthsSet.has(period)
      ) {
        return
      }

      if (!groupedByTarget.has(targetIdLibro)) {
        groupedByTarget.set(targetIdLibro, new Set())
      }

      groupedByTarget.get(targetIdLibro).add(bankIdLibro)
    })

    for (const [targetIdLibro, bankIdsSet] of groupedByTarget.entries()) {
      const bankIds = [...bankIdsSet]

      if (bankIds.length === 0) continue

      const dto = {
        datoBanco: {
          ids: bankIds
        },
        datoBoleta: {
          ids: []
        },
        datoCaja: {},
        datoCajaLastYear: {},
        instrucciones: [
          true,
          false,
          false,
          false
        ]
      }

      console.info(
        '[E2E][Remuneraciones] Persistiendo conciliación nativa Luca',
        {
          businessId,
          targetIdLibro,
          bankIds
        }
      )

      try {
        await organizationApi.postForInOne(
          {
            businessId: Number(businessId),
            idLibro: targetIdLibro
          },
          dto
        )

        console.info(
          '[E2E][Remuneraciones] Conciliación persistida correctamente en Luca',
          {
            targetIdLibro,
            bankIds
          }
        )
      } catch (error) {
        console.error(
          '[E2E][Remuneraciones] No fue posible persistir este target en Luca; continuando con el siguiente',
          {
            targetIdLibro,
            bankIds,
            status: error?.response?.status,
            response: error?.response?.data
          }
        )
      }
    }
  }

  const getTransferReconciliationSummary = async businessId => {
    if (!businessId) return { items: [] }

    const params = new URLSearchParams({
      businessId: String(businessId)
    })

    const response = await fetch(
      `${REACT_APP_RECONCILIATION_API_URL}/traspasos/conciliacion/summary?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      }
    )

    if (!response.ok) {
      throw new Error(
        `Error consultando conciliación de traspasos: ${response.status}`
      )
    }

    return response.json()
  }

  const persistTransferReconciliationsInLuca = async (
    businessId,
    items
  ) => {
    if (!businessId || !Array.isArray(items) || items.length === 0) return

    const groupedByRelation = new Map()

    items.forEach(item => {
      const relationKey = String(item?.relationKey || '')
      const idLibro = Number(item?.idLibro)
      const side = String(item?.side || '').toLowerCase()

      if (
        item?.isReconciled !== true ||
        !relationKey ||
        !Number.isFinite(idLibro) ||
        (side !== 'cargo' && side !== 'abono')
      ) {
        return
      }

      if (!groupedByRelation.has(relationKey)) {
        groupedByRelation.set(relationKey, {
          cargoIdLibro: null,
          abonoIdLibro: null
        })
      }

      const relation = groupedByRelation.get(relationKey)

      if (side === 'cargo') {
        relation.cargoIdLibro = idLibro
      }

      if (side === 'abono') {
        relation.abonoIdLibro = idLibro
      }
    })

    for (const [relationKey, relation] of groupedByRelation.entries()) {
      const cargoIdLibro = Number(relation?.cargoIdLibro)
      const abonoIdLibro = Number(relation?.abonoIdLibro)

      if (
        !Number.isFinite(cargoIdLibro) ||
        !Number.isFinite(abonoIdLibro)
      ) {
        console.warn(
          '[E2E][Traspasos] Relación incompleta; no se persiste en Luca',
          {
            relationKey,
            cargoIdLibro: relation?.cargoIdLibro,
            abonoIdLibro: relation?.abonoIdLibro
          }
        )
        continue
      }

      console.info(
        '[E2E][Traspasos] Persistiendo conciliación nativa Luca',
        {
          businessId,
          relationKey,
          abonoIdLibro,
          cargoIdLibro
        }
      )

      try {
        await organizationApi.postListaVincular(
          {
            businessId: Number(businessId),
            idLibro: abonoIdLibro
          },
          {
            ids: [cargoIdLibro]
          }
        )

        console.info(
          '[E2E][Traspasos] Conciliación persistida correctamente en Luca',
          {
            relationKey,
            abonoIdLibro,
            cargoIdLibro
          }
        )
      } catch (error) {
        console.error(
          '[E2E][Traspasos] No fue posible persistir esta relación en Luca; continuando con la siguiente',
          {
            relationKey,
            abonoIdLibro,
            cargoIdLibro,
            status: error?.response?.status,
            response: error?.response?.data
          }
        )
      }
    }
  }

  const getF29ReconciliationBankSummary = async businessId => {
    if (!businessId) return { items: [] }

    const params = new URLSearchParams({
      businessId: String(businessId)
    })

    const response = await fetch(
      `${REACT_APP_RECONCILIATION_API_URL}/f29/conciliacion/bank-summary?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      }
    )

    if (!response.ok) {
      throw new Error(
        `Error consultando conciliación bancaria de F29: ${response.status}`
      )
    }

    return response.json()
  }

  const persistF29ReconciliationsInLuca = async (
    businessId,
    items,
    allowedMonths
  ) => {
    if (!businessId || !Array.isArray(items) || items.length === 0) return

    const allowedMonthsSet = new Set(allowedMonths || [])
    const groupedByTarget = new Map()

    items.forEach(item => {
      const targetIdLibro = Number(item?.target?.idLibro)
      const bankIdLibro = Number(item?.idLibro)
      const period = String(item?.period || '')

      if (
        item?.isReconciled !== true ||
        !Number.isFinite(targetIdLibro) ||
        !Number.isFinite(bankIdLibro) ||
        !allowedMonthsSet.has(period)
      ) {
        return
      }

      if (!groupedByTarget.has(targetIdLibro)) {
        groupedByTarget.set(targetIdLibro, new Set())
      }

      groupedByTarget.get(targetIdLibro).add(bankIdLibro)
    })

    for (const [targetIdLibro, bankIdsSet] of groupedByTarget.entries()) {
      const bankIds = [...bankIdsSet]

      if (bankIds.length === 0) continue

      const dto = {
        datoBanco: {
          ids: bankIds
        },
        datoBoleta: {
          ids: []
        },
        datoCaja: {},
        datoCajaLastYear: {},
        instrucciones: [
          true,
          false,
          false,
          false
        ]
      }

      console.info(
        '[E2E][F29] Persistiendo conciliación nativa Luca',
        {
          businessId,
          targetIdLibro,
          bankIds
        }
      )

      try {
        await organizationApi.postForInOne(
          {
            businessId: Number(businessId),
            idLibro: targetIdLibro
          },
          dto
        )

        console.info(
          '[E2E][F29] Conciliación persistida correctamente en Luca',
          {
            targetIdLibro,
            bankIds
          }
        )
      } catch (error) {
        console.error(
          '[E2E][F29] No fue posible persistir este target en Luca; continuando con el siguiente',
          {
            targetIdLibro,
            bankIds,
            status: error?.response?.status,
            response: error?.response?.data
          }
        )
      }
    }
  }

  const isAutomaticConciliationSwitchDisabled =
    isCheckingAutomaticConciliation ||
    isUpdatingAutomaticConciliation ||
    isRunningRemunerationConciliation ||
    isRunningTransferConciliation ||
    isRunningF29Conciliation ||
    !currentBusiness?.id ||
    !hasBankLinked

  const getAutomaticReconciliationMonths = () => {
    if (autoConciliationPeriod === 'current-year') {
      return Array.from(
        { length: currentMonth },
        (_, index) =>
          `${String(index + 1).padStart(2, '0')}-${currentYear}`
      )
    }

    return [
      `${String(autoConciliationMonth).padStart(2, '0')}-${currentYear}`
    ]
  }

  const runRemunerationReconciliation = async () => {
    if (!currentBusiness?.id) return null

    const params = new URLSearchParams({
      businessId: String(currentBusiness.id)
    })

    const months = getAutomaticReconciliationMonths()

    months.forEach(month => {
      params.append('months', month)
    })

    const response = await fetch(
      `${REACT_APP_RECONCILIATION_API_URL}/remuneraciones/conciliacion/run?${params.toString()}`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        }
      }
    )

    if (!response.ok) {
      throw new Error(
        `Error ejecutando conciliación de remuneraciones: ${response.status}`
      )
    }

    return response.json()
  }

  const runTransferReconciliation = async () => {
    if (!currentBusiness?.id) return null

    const params = new URLSearchParams({
      businessId: String(currentBusiness.id)
    })

    const months = getAutomaticReconciliationMonths()

    months.forEach(month => {
      params.append('months', month)
    })

    const response = await fetch(
      `${REACT_APP_RECONCILIATION_API_URL}/traspasos/conciliacion/run?${params.toString()}`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        }
      }
    )

    if (!response.ok) {
      throw new Error(
        `Error ejecutando conciliación de traspasos: ${response.status}`
      )
    }

    return response.json()
  }

  const runF29Reconciliation = async () => {
    if (!currentBusiness?.id) return null

    const params = new URLSearchParams({
      businessId: String(currentBusiness.id)
    })

    const months = getAutomaticReconciliationMonths()

    months.forEach(month => {
      params.append('months', month)
    })

    const response = await fetch(
      `${REACT_APP_RECONCILIATION_API_URL}/f29/conciliacion/run?${params.toString()}`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        }
      }
    )

    if (!response.ok) {
      throw new Error(
        `Error ejecutando conciliación F29: ${response.status}`
      )
    }

    return response.json()
  }

  const updateAutomaticConciliationState = async (
    type,
    options = { silent: false, skipCurrentBusinessSync: false }
  ) => {
    const { silent = false, skipCurrentBusinessSync = false, startDate } = options
    const businessId = currentBusiness?.id

    if (!currentBusiness?.id) {
      sileo.error({
        title: 'Error',
        description: (
          <span className='text-red-500/80 font-medium'>
            No se pudo identificar la empresa seleccionada.
          </span>
        )
      })
      return false
    }

    setIsUpdatingAutomaticConciliation(true)
    try {
      const reconciliationStartDate =
        startDate ?? (autoConciliationPeriod === 'current-year'
          ? `${currentYear}-01-01`
          : `${currentYear}-${String(autoConciliationMonth).padStart(2, '0')}-01`)

      await bankAPI.setAutomaticConciliationState(
        businessId,
        type,
        reconciliationStartDate,
        true
      )
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        'No pudimos actualizar el estado de conciliación automática.'
      sileo.error({
        title: 'No se pudo actualizar',
        description: <span className='text-red-500/80 font-medium'>{message}</span>
      })
      return false
    } finally {
      setIsUpdatingAutomaticConciliation(false)
    }

    try {
      if (String(activeBusinessRef.current?.id) !== String(businessId)) return true
      const isAuto = type === 'auto'
      setIsAutomaticConciliation(isAuto)
      if (currentBusiness && !skipCurrentBusinessSync) {
        setCurrentBusiness({ ...activeBusinessRef.current, conciliacionAutomatica: isAuto })
      }
      if (!silent) {
        sileo.success({
          title: isAuto
            ? 'Conciliación automática activada'
            : 'Conciliación automática desactivada',
          description: (
            <span className='text-green-500/80 font-medium'>
              {isAuto
                ? 'La conciliación automática quedó activa para esta empresa.'
                : 'La conciliación automática quedó desactivada para esta empresa.'}
            </span>
          )
        })
      }
      return true
    } catch (error) {
      console.error('Error aplicando estado local de conciliación automática:', error)
      return false
    }
  }

  const handleAutomaticConciliationClick = async () => {
    if (automaticConciliationClickPending.current || isAutomaticConciliationSwitchDisabled) return

    if (isAutomaticConciliation) {
      setAutoConciliationAction('deactivate')
      setAutoConciliationStep(1)
      setAutoConciliationPeriod('current-month')
      setAutoConciliationMonth(currentMonth)
      setShowAutoConciliationModal(true)
      return
    }

    const businessId = currentBusiness.id
    automaticConciliationClickPending.current = true
    setIsCheckingAutomaticConciliation(true)
    try {
      const response = await organizationApi.getMyBusinesses()
      if (String(activeBusinessRef.current?.id) !== String(businessId)) return
      const startDate = getSavedBankConciliationDate(response?.data, businessId)
      if (startDate) {
        await updateAutomaticConciliationState('auto', { startDate })
        return
      }
      setAutoConciliationAction('activate')
      setAutoConciliationStep(1)
      setAutoConciliationPeriod('current-month')
      setAutoConciliationMonth(currentMonth)
      setShowAutoConciliationModal(true)
    } catch (error) {
      if (String(activeBusinessRef.current?.id) !== String(businessId)) return
      sileo.error({ title: 'No se pudo activar', description: error?.message || 'No pudimos consultar la empresa seleccionada.' })
    } finally {
      automaticConciliationClickPending.current = false
      setIsCheckingAutomaticConciliation(false)
    }
  }

  const handleCloseAutoConciliationModal = () => {
    if (
      isUpdatingAutomaticConciliation ||
      isRunningRemunerationConciliation ||
      isRunningTransferConciliation ||
      isRunningF29Conciliation
    ) {
      return
    }

    setShowAutoConciliationModal(false)
    setAutoConciliationAction(null)
    setAutoConciliationStep(1)
    setAutoConciliationPeriod('current-month')
    setAutoConciliationMonth(currentMonth)
  }

  const handlePreviousAutoConciliationStep = () => {
    if (
      isUpdatingAutomaticConciliation ||
      autoConciliationAction !== 'activate' ||
      autoConciliationStep <= 1
    ) {
      return
    }

    setAutoConciliationStep(currentStep => currentStep - 1)
  }

  const handleConfirmAutomaticConciliationModal = async () => {
    if (autoConciliationAction === 'activate' && autoConciliationStep === 1) {
      setAutoConciliationStep(2)
      return
    }

    if (autoConciliationAction === 'activate' && autoConciliationStep === 2) {
      setAutoConciliationStep(3)
      return
    }

    if (autoConciliationAction === 'activate' && autoConciliationStep === 3) {
      setAutoConciliationStep(4)
      return
    }

    const type =
      autoConciliationAction === 'deactivate'
        ? 'no-auto'
        : 'auto'

    // --------------------------------------------
    // ACTIVACIÓN:
    // primero Remuneraciones / Previred E2E
    // --------------------------------------------
    if (type === 'auto') {
      try {
        setIsRunningRemunerationConciliation(true)

        const reconciliationMonths =
          getAutomaticReconciliationMonths()

        await runRemunerationReconciliation()

        const remunerationSummary =
          await getRemunerationReconciliationBankSummary(
            currentBusiness.id
          )

        const remunerationItems =
          Array.isArray(remunerationSummary?.items)
            ? remunerationSummary.items
            : []

        await persistRemunerationReconciliationsInLuca(
          currentBusiness.id,
          remunerationItems,
          reconciliationMonths
        )
      } catch (error) {
        console.error(
          'No fue posible ejecutar la conciliación automática de remuneraciones:',
          error
        )
      } finally {
        setIsRunningRemunerationConciliation(false)
      }
    }

    // --------------------------------------------
    // ACTIVACIÓN:
    // luego Formulario 29 E2E
    // --------------------------------------------
    if (type === 'auto') {
      try {
        setIsRunningF29Conciliation(true)

        const reconciliationMonths =
          getAutomaticReconciliationMonths()

        await runF29Reconciliation()

        const f29Summary =
          await getF29ReconciliationBankSummary(
            currentBusiness.id
          )

        const f29Items =
          Array.isArray(f29Summary?.items)
            ? f29Summary.items
            : []

        await persistF29ReconciliationsInLuca(
          currentBusiness.id,
          f29Items,
          reconciliationMonths
        )

      } catch (error) {
        console.error(
          'No fue posible ejecutar la conciliación automática F29:',
          error
        )
      } finally {
        setIsRunningF29Conciliation(false)
      }
    }

    // --------------------------------------------
    // ACTIVACIÓN:
    // luego Traspasos entre cuentas E2E
    // --------------------------------------------
    if (type === 'auto') {
      try {
        setIsRunningTransferConciliation(true)

        await runTransferReconciliation()

        const transferSummary =
          await getTransferReconciliationSummary(
            currentBusiness.id
          )

        const transferItems =
          Array.isArray(transferSummary?.items)
            ? transferSummary.items
            : []

        await persistTransferReconciliationsInLuca(
          currentBusiness.id,
          transferItems
        )

      } catch (error) {
        console.error(
          'No fue posible ejecutar la conciliación automática de traspasos:',
          error
        )
      } finally {
        setIsRunningTransferConciliation(false)
      }
    }

    // --------------------------------------------
    // Recién ahora activamos / desactivamos Luca
    // --------------------------------------------
    const wasUpdated =
      await updateAutomaticConciliationState(type)

    if (!wasUpdated) {
      return
    }

    handleCloseAutoConciliationModal()
  }


  return { isAutomaticConciliation, isUpdatingAutomaticConciliation, isCheckingAutomaticConciliation, isRunningRemunerationConciliation, isRunningTransferConciliation, isRunningF29Conciliation, showAutoConciliationModal, autoConciliationAction, autoConciliationStep, autoConciliationPeriod, autoConciliationMonth, setAutoConciliationPeriod, setAutoConciliationMonth, isAutomaticConciliationSwitchDisabled, updateAutomaticConciliationState, handleAutomaticConciliationClick, handleCloseAutoConciliationModal, handlePreviousAutoConciliationStep, handleConfirmAutomaticConciliationModal }
}
